import type {
  ApproveLeaveAdjustmentsDTO,
  CreateLeaveRequestDTO,
  CreateOvertimeRequestDTO,
  LeaveBalanceItemDTO,
  LeaveRequestDTO,
  LeaveRequestListQueryDTO,
  LeaveTypeDTO,
  LineManagerLeaveListQueryDTO,
  OtRuleDTO,
  OvertimeListQueryDTO,
  OvertimeRequestDTO,
  RecentActivityItemDTO,
  UpdateLeaveRequestDTO,
} from "@/modules/hr/domain/contracts";
import type {
  LeaveRequestByIdResult,
  LeaveRequestsListResult,
  LineManagerLeaveListResult,
  LineManagerLeaveRequestDetail,
  LineManagerQueueStats,
  OvertimeRequestByIdResult,
  OvertimeRequestsListResult,
  PayrollByIdResult,
  PayrollListResult,
  SalaryAdvanceDetail,
  SalaryAdvanceListItem,
} from "@/modules/hr/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const base = () => `${config.apiUrl}/hr`;

function toFormData(input: Record<string, unknown>): FormData {
  const fd = new FormData();
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null) continue;
    if (v instanceof File) fd.append(k, v);
    else fd.append(k, String(v));
  }
  return fd;
}

export const hrApi = {
  leave: {
    getTypes(): Promise<LeaveTypeDTO[]> {
      return fetcher.get(`${base()}/leave/types`);
    },
    getBalance(year?: number): Promise<LeaveBalanceItemDTO[]> {
      const url = new URL(`${base()}/leave/balance`);
      if (year != null) url.searchParams.set("year", String(year));
      return fetcher.get(url.toString());
    },
    list(query: LeaveRequestListQueryDTO): Promise<LeaveRequestsListResult> {
      const url = new URL(`${base()}/leave/requests`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.status) url.searchParams.set("status", query.status);
      if (query.sortBy) url.searchParams.set("sortBy", query.sortBy);
      if (query.sortOrder) url.searchParams.set("sortOrder", query.sortOrder);
      return fetcher.get(url.toString());
    },
    getById(id: string): Promise<LeaveRequestByIdResult> {
      return fetcher.get(`${base()}/leave/requests/${id}`);
    },
    create(input: CreateLeaveRequestDTO): Promise<LeaveRequestDTO> {
      return fetcher.postForm(
        `${base()}/leave/requests`,
        toFormData({
          leaveTypeId: input.leaveTypeId,
          startDate: input.startDate,
          endDate: input.endDate,
          reason: input.reason ?? "",
          ...(input.attachmentKey !== undefined && {
            attachmentKey: input.attachmentKey ?? "",
          }),
        }),
      );
    },
    update(
      id: string,
      input: Partial<UpdateLeaveRequestDTO>,
    ): Promise<LeaveRequestDTO> {
      return fetcher.patchForm(
        `${base()}/leave/requests/${id}`,
        toFormData({
          ...(input.leaveTypeId != null && { leaveTypeId: input.leaveTypeId }),
          ...(input.startDate != null && { startDate: input.startDate }),
          ...(input.endDate != null && { endDate: input.endDate }),
          ...(input.reason !== undefined && { reason: input.reason ?? "" }),
          ...(input.attachmentKey !== undefined && {
            attachmentKey: input.attachmentKey ?? "",
          }),
        }),
      );
    },
    delete(id: string): Promise<{ ok: boolean }> {
      return fetcher.delete(`${base()}/leave/requests/${id}`);
    },
    lineManager: {
      status(): Promise<LineManagerQueueStats> {
        return fetcher.get(`${base()}/leave/line-manager/status`);
      },
      list(
        query: LineManagerLeaveListQueryDTO,
      ): Promise<LineManagerLeaveListResult> {
        const url = new URL(`${base()}/leave/line-manager/requests`);
        url.searchParams.set("limit", String(query.limit ?? 20));
        url.searchParams.set("offset", String(query.offset ?? 0));
        if (query.status) url.searchParams.set("status", query.status);
        if (query.sortBy) url.searchParams.set("sortBy", query.sortBy);
        if (query.sortOrder) url.searchParams.set("sortOrder", query.sortOrder);
        return fetcher.get(url.toString());
      },
      getById(id: string): Promise<LineManagerLeaveRequestDetail> {
        return fetcher.get(`${base()}/leave/line-manager/requests/${id}`);
      },
      approveLine(
        id: string,
        input?: ApproveLeaveAdjustmentsDTO,
      ): Promise<{ ok: boolean }> {
        return fetcher.patch(
          `${base()}/leave/line-manager/requests/${id}/approve-line`,
          input ?? {},
        );
      },
      rejectLine(
        id: string,
        input: { rejectionReason?: string | null },
      ): Promise<{ ok: boolean }> {
        return fetcher.patch(
          `${base()}/leave/line-manager/requests/${id}/reject-line`,
          input,
        );
      },
    },
  },
  overtime: {
    getRules(): Promise<OtRuleDTO[]> {
      return fetcher.get(`${base()}/overtime/rules`);
    },
    list(query: OvertimeListQueryDTO): Promise<OvertimeRequestsListResult> {
      const url = new URL(`${base()}/overtime/requests`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.status) url.searchParams.set("status", query.status);
      if (query.sortBy) url.searchParams.set("sortBy", query.sortBy);
      if (query.sortOrder) url.searchParams.set("sortOrder", query.sortOrder);
      return fetcher.get(url.toString());
    },
    getById(id: string): Promise<OvertimeRequestByIdResult> {
      return fetcher.get(`${base()}/overtime/requests/${id}`);
    },
    create(input: CreateOvertimeRequestDTO): Promise<OvertimeRequestDTO> {
      return fetcher.post(`${base()}/overtime/requests`, input);
    },
  },
  payroll: {
    list(
      limit?: number,
      offset?: number,
      year?: number,
    ): Promise<PayrollListResult> {
      const url = new URL(`${base()}/payroll`);
      if (limit != null) url.searchParams.set("limit", String(limit));
      if (offset != null) url.searchParams.set("offset", String(offset));
      if (year != null) url.searchParams.set("year", String(year));
      return fetcher.get(url.toString());
    },
    getById(id: string): Promise<PayrollByIdResult> {
      return fetcher.get(`${base()}/payroll/${id}`);
    },
  },
  salaryAdvance: {
    list(query: { offset?: number; limit?: number; status?: string }): Promise<{
      data: SalaryAdvanceListItem[];
      meta: { total: number; offset: number; limit: number };
    }> {
      const url = new URL(`${base()}/salary-advance/me/requests`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.status) url.searchParams.set("status", query.status);
      return fetcher.get(url.toString());
    },
    getById(id: string): Promise<SalaryAdvanceDetail> {
      return fetcher.get(`${base()}/salary-advance/me/requests/${id}`);
    },
    create(input: {
      requestDate: string;
      amount: number;
      reason?: string | null;
    }): Promise<{ id: string }> {
      return fetcher.post(`${base()}/salary-advance/me/requests`, input);
    },
    cancel(id: string): Promise<{ ok: boolean }> {
      return fetcher.patch(
        `${base()}/salary-advance/me/requests/${id}/cancel`,
        {},
      );
    },
  },
  activity: {
    recent(limit?: number): Promise<RecentActivityItemDTO[]> {
      const url = new URL(`${base()}/activity/recent`);
      if (limit != null) url.searchParams.set("limit", String(limit));
      return fetcher.get(url.toString());
    },
  },
};
